import { useEffect, useMemo, useState } from "react";

import { Box } from "@mui/material";
import { withTranslation } from "react-i18next";
import CodeUtil from "@plannel/pages/utils/code-util";
import {groupBy, isEmpty, isEqual, isNumber, sum, mean} from "lodash";
import dashboardService from "@plannel/services/dashboard/dashboard-service";
import dateUtils from "@plannel/utils/date-util";
import TypeToggleButton from "@plannel/components/ToggleButton";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { DashboardPeriodCaption, baseDatesToPeriod } from "@plannel/components/dashboard/DashboardLayout";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { IPLocationFilter } from "@plannel/components/filter/LocationFilter";
import reduxUtil from "@plannel/utils/redux-util";
import DashboardComponent from "@plannel/components/layout/DashboardComponent";
import { useDispatch } from "react-redux";

const { toggleType: { QTY, AMT } } = CodeUtil;

const ITEM_HRCHY_YTD = "ITEM_HRCHY_YTD";
const LOCATION_YTD = "LOCATION_YTD";
const MONTHLY_TREND = "MONTHLY_TREND";

const IP_TARGET_STOCK = "IP_TARGET_STOCK";
const IP_STOCKOUT = "IP_STOCKOUT";
const IP_EXCESS = "IP_EXCESS";
const IP_SLOW_MOVING = "IP_SLOW_MOVING";

const SALES_ACTUAL = "actualSales";
const INV_ACTUAL = "actualInv";
const TARGET_STOCK = "targetStock";
const VS_TARGET = "targetVsInvPer";
const INV_TURNOVER = "invTurnover";
const LAST_YEAR_INV_TURNOVER = "lastYearInvTurnover";
const YOY = "yoyPer";
const MOM = "momPer";

// YOY - Inventory Turnover 전용
const YOY_SALES_QTY = "yoySalesQtyPivot";
const YOY_SALES_AMT = "yoySalesAmtPivot";
const YOY_SALES_COGS = "yoySalesCogsPivot";
const YOY_INV_QTY = "yoyInvQtyPivot";
const YOY_INV_AMT = "yoyInvAmtPivot";

const INV_TURNOVER_COGS = "invTurnoverCogs";
const LAST_YEAR_INV_TURNOVER_COGS = "lastYearInvTurnoverCogs";

const IP_DASHBOARD_MEASURES_QTY = [SALES_ACTUAL, INV_ACTUAL, TARGET_STOCK, VS_TARGET, INV_TURNOVER, LAST_YEAR_INV_TURNOVER, YOY, MOM];
const IP_DASHBOARD_MEASURES_AMT= [SALES_ACTUAL, INV_ACTUAL, TARGET_STOCK, VS_TARGET, INV_TURNOVER, INV_TURNOVER_COGS, LAST_YEAR_INV_TURNOVER, LAST_YEAR_INV_TURNOVER_COGS, YOY, MOM];
const IP_DASHBOARD_MEAUSRES_MAPPING_OBJ = {
  [QTY]: {
    [SALES_ACTUAL]: "salesQtyPivot",
    [INV_ACTUAL]: "inventoryQtyPivot",
    [TARGET_STOCK]: "targetStockQtyPivot",
    [VS_TARGET]: "vsTargetStockQtyPivot",
    [INV_TURNOVER]: "invTurnoverQtyPivot",
    [LAST_YEAR_INV_TURNOVER]: "lastYearInvTurnoverQtyPivot",
    [YOY]: "yoyInvTurnoverQtyPivot",
    [MOM]: "momInvQtyPivot",
  },
  [AMT]: {
    [SALES_ACTUAL]: "salesAmtPivot",
    [INV_ACTUAL]: "inventoryAmtPivot",
    [TARGET_STOCK]: "targetStockAmtPivot",
    [VS_TARGET]: "vsTargetStockAmtPivot",
    [INV_TURNOVER]: "invTurnoverAmtPivot",
    [INV_TURNOVER_COGS]: "invTurnoverCogsPivot",
    [LAST_YEAR_INV_TURNOVER]: "lastYearInvTurnoverAmtPivot",
    [LAST_YEAR_INV_TURNOVER_COGS]: "lastYearInvTurnoverCogsPivot",
    [YOY]: "yoyInvTurnoverAmtPivot",
    [MOM]: "momInvAmtPivot",
  }
};

const digit = 5;
const IPDashboard = ({ title, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { IP } = CodeUtil.module;

  const [locationFilter, setLocationFilter] = useState(reduxViewState?.location ? reduxViewState.location : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ? reduxViewState.filter : null);
  const [measureType, setMeasureType] = useState(reduxViewState?.measureType ?? AMT);

  const [trendDataResult, setTrendDataResult] = useState([]);
  const [trendChartResult, setTrendChartResult] = useState([]);
  const [ytdDashboardResult, setYtdDashboardResult] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currency, setCurrency] = useState(null);

  const ipDashboardMeasures = useMemo(() => ({
    [QTY]: IP_DASHBOARD_MEASURES_QTY,
    [AMT]: IP_DASHBOARD_MEASURES_AMT,
  }), []);

  const stackBarColumDefs = useMemo(() => [
    {
      headerName: "realCategory", field: "hrchyNm", width: 70, flex: 1,
      type: ["nonEditableColumn", "defaultValueFormatter"],
    },
    {
      headerName: "",
      field: "stackBarChartData",
      width: 500,
      cellRenderer: 'stackBarChartCellRenderer',
      cellRendererParams: {
        scmModule: IP,
        flipZIndex: true,
        currencyCd: currency
      },
      type: ["nonEditableColumn", "defaultValueFormatter"],
      flex: 4
    }
  ], [IP, currency]);

  const getRequestParams = () => {
    const params = {};

    if (filterValue?.category) {
      params.itemHrchyCd = filterValue.category.hrchyCd;
    }

    if (locationFilter) {
      params.locName = locationFilter.name;
    }
    params.currentDate = new ZDate();

    return { ...params };
  }

  const getIdxCurrentMonth = dates => {
    const today = new Date();
    const year = today.getFullYear();
    // today기준 저번달을 가져옴
    const month = String(today.getMonth()).padStart(2, '0');
    const currentYearMonth = `${year}-${month}`; // ex: '2025-10'

    return dates.findIndex(dateString => {
      const yearMonthInArray = dateString.substring(0, 7);
      return yearMonthInArray === currentYearMonth;
    });
  };

  const setMonthlyByTrendData = (results, headers) => {
    if (isEmpty(results)) {
      return {};
    }

    const result = results[0];

    headers = headers.map(header => dateUtils.formatDate(header, "yyyy-MM"));

    const itemHrchyName = result.itemHrchyName;
    const locationName = result.locationName;
    const trendDashboardData = {
      baseDate: headers,
      qty: [],
      amt: []
    };

    const qtyMappingObj = IP_DASHBOARD_MEAUSRES_MAPPING_OBJ[QTY];
    const amtMappingObj = IP_DASHBOARD_MEAUSRES_MAPPING_OBJ[AMT];

    const isQty = isEqual(measureType, QTY);
    const activeMeasures = isQty ? IP_DASHBOARD_MEASURES_QTY : IP_DASHBOARD_MEASURES_AMT;
    const idxCurrent = getIdxCurrentMonth(headers);

    for (const measure of activeMeasures) {
      let customAmtTotal = null;
      let customQtyTotal = null;
      let customCogsTotal = null;
      if (measure === VS_TARGET) {
        // Actual Inventory ÷ Target Inventory x 100
        customQtyTotal = sum(result?.[qtyMappingObj[INV_ACTUAL]]?.slice(0, idxCurrent + 1)) / sum(result?.[qtyMappingObj[TARGET_STOCK]]);
        customAmtTotal = sum(result?.[amtMappingObj[INV_ACTUAL]]?.slice(0, idxCurrent + 1)) / sum(result?.[amtMappingObj[TARGET_STOCK]]);
      } else if (measure === INV_TURNOVER || measure === INV_TURNOVER_COGS) {
        // 【Annual total sales for the current fiscal year】
        // actual sales total / actual inventory average
        const salesTotalQty = sum(result?.[qtyMappingObj[SALES_ACTUAL]]?.slice(0, idxCurrent + 1));
        const salesTotalAmt = sum(result?.[amtMappingObj[SALES_ACTUAL]]?.slice(0, idxCurrent + 1));
        const invAvgQty = mean((result?.[qtyMappingObj[INV_ACTUAL]] || []).slice(0, idxCurrent + 1));
        const invAvgAmt = mean((result?.[amtMappingObj[INV_ACTUAL]] || []).slice(0, idxCurrent + 1));

        customQtyTotal = salesTotalQty / invAvgQty;
        customAmtTotal = salesTotalAmt / invAvgAmt;

        if (measure === INV_TURNOVER_COGS) {
          customCogsTotal = "-";
        }
      } else if (measure === LAST_YEAR_INV_TURNOVER || measure === LAST_YEAR_INV_TURNOVER_COGS) { // YOY - Inventory Turnover 전용
        const idxCurrent = getIdxCurrentMonth(headers);
        const yoySalesTotalQty = sum(result?.[YOY_SALES_QTY]);
        const yoySalesTotalAmt = sum(result?.[YOY_SALES_AMT]);
        const yoyBohQty = result?.preMonLastYearFiscalInvQty || 0;
        const yoyEohQty = result?.[YOY_INV_QTY]?.[idxCurrent] || 0;
        const yoyBohAmt = result?.preMonLastYearFiscalInvAmt || 0;
        const yoyEohAmt = result?.[YOY_INV_AMT]?.[idxCurrent] || 0;

        customQtyTotal = yoySalesTotalQty / ((yoyBohQty + yoyEohQty) / 2);
        customAmtTotal = yoySalesTotalAmt / ((yoyBohAmt + yoyEohAmt) / 2);

        if (measure === LAST_YEAR_INV_TURNOVER_COGS) {
          const yoySalesCogsTotal = sum(result?.[YOY_SALES_COGS]);

          customCogsTotal = yoySalesCogsTotal / ((yoyBohAmt + yoyEohAmt) / 2);
        }
      }

      if (isNumber(customQtyTotal)) {
        customQtyTotal = customQtyTotal.toFixed(digit);
      }

      if (isNumber(customAmtTotal)) {
        customAmtTotal = customAmtTotal.toFixed(digit);
      }
      
      if (isNumber(customCogsTotal)) {
        customCogsTotal = customCogsTotal.toFixed(digit);
      }

      const pivotQtyValue = {};
      const pivotAmtValue = {};
      const qtyDataKey = qtyMappingObj[measure];
      const amtDataKey = amtMappingObj[measure];

      for (let idx = 0, len = headers.length; idx < len; idx++) {
        const header = headers[idx];
        pivotQtyValue[header] = result[qtyDataKey]?.[idx] || 0;
        pivotAmtValue[header] = result[amtDataKey]?.[idx] || 0;
      }

      const qtyObj = {
        itemHrchy: itemHrchyName,
        location: locationName,
        measure,
        ...pivotQtyValue,
        customQtyTotal
      };

      const amtObj = {
        itemHrchy: itemHrchyName,
        location: locationName,
        measure,
        ...pivotAmtValue,
        customAmtTotal,
        customCogsTotal
      };

      trendDashboardData.qty.push(qtyObj);
      trendDashboardData.amt.push(amtObj);
    }

    return trendDashboardData;
  }

  const sortByData = (a, b) => {
    // HrchyNm이 "ALL"인 경우 항상 먼저 오도록 설정
    if (a.hrchyNm === "ALL") return -1;
    if (b.hrchyNm === "ALL") return 1;

    const aHasBoth = a.stackBarChartData[0] !== 0 && a.stackBarChartData[1] !== 0;
    const bHasBoth = b.stackBarChartData[0] !== 0 && b.stackBarChartData[1] !== 0;

    // [0], [1] 값이 모두 존재하는 항목을 우선적으로 배치
    if (aHasBoth && !bHasBoth) return -1; // a가 우선
    if (!aHasBoth && bHasBoth) return 1;  // b가 우선

    // [0], [1] 둘 다 존재하는 경우는 [0] 값으로 내림차순 정렬
    if (aHasBoth && bHasBoth) {
      return b.stackBarChartData[0] - a.stackBarChartData[0];
    }

    const compareValue = b.stackBarChartData[0] - a.stackBarChartData[0];

    // [0] 값이 같은 경우 [1] 값으로 내림차순 정렬
    if (compareValue === 0) {
      return b.stackBarChartData[1] - a.stackBarChartData[1];
    }

    // [0], [1] 중 하나만 존재하는 경우 [0] 값으로 내림차순 정렬
    return compareValue;
  };

  // 최대 값이 높은 순서대로 정렬
  const sortYtdData = (ytdDataMap) => {
    for (const ytd in ytdDataMap) {
      if(Object.prototype.hasOwnProperty.call(ytdDataMap, ytd)) {
        ytdDataMap[ytd].sort(sortByData);
      }
    }
  }

  const setYtdDomainRangeDataExcludeAll = (ytdDataMap) => {
    for (const ytd in ytdDataMap) {
      if (Object.prototype.hasOwnProperty.call(ytdDataMap, ytd)) {
        const ytdByTypeGroupObj = groupBy(ytdDataMap[ytd], "type");

        /* QTY */
        const qtyYtdDataArray = ytdByTypeGroupObj?.[QTY] ?? [];
        const qtyMaxValueNotAll = Math.max(...qtyYtdDataArray.filter(data => data.hrchyNm !== "ALL").map(data => data?.stackBarChartData[0]));
        qtyYtdDataArray.forEach(data => {
          if (data.hrchyNm !== "ALL") {
            data.domainMaxValue = qtyMaxValueNotAll;
          }
        });
          
        /* AMT */
        const amtYtdDataArray = ytdByTypeGroupObj?.[AMT] ?? [];
        const amtMaxValueNotAll = Math.max(...amtYtdDataArray.filter(data => data.hrchyNm !== "ALL").map(data => data?.stackBarChartData[0]));
        amtYtdDataArray.forEach(data => {
          if (data.hrchyNm !== "ALL") {
            data.domainMaxValue = amtMaxValueNotAll;
          }
        });
      }
    }
  }

  const setYtdData = (itemHrchyYtd, locationYtd) => {
    sortYtdData(itemHrchyYtd);
    sortYtdData(locationYtd);

    setYtdDomainRangeDataExcludeAll(itemHrchyYtd);
    setYtdDomainRangeDataExcludeAll(locationYtd);

    const itemHrchyYtdObj = {
      [IP_TARGET_STOCK]: itemHrchyYtd?.[IP_TARGET_STOCK],
      [IP_STOCKOUT]: itemHrchyYtd?.[IP_STOCKOUT],
      [IP_EXCESS]: itemHrchyYtd?.[IP_EXCESS],
      [IP_SLOW_MOVING]: itemHrchyYtd?.[IP_SLOW_MOVING]
    }

    const locationYtdObj = {
      [IP_TARGET_STOCK]: locationYtd?.[IP_TARGET_STOCK],
      [IP_STOCKOUT]: locationYtd?.[IP_STOCKOUT],
      [IP_EXCESS]: locationYtd?.[IP_EXCESS],
      [IP_SLOW_MOVING]: locationYtd?.[IP_SLOW_MOVING]
    }

    const ytdDashboardData = {
      itemHrchy: itemHrchyYtdObj,
      location: locationYtdObj
    }

    return ytdDashboardData;
  }

  const transformTrendChartData = (trendDashboardResult) => {
    const chartData = [];
    const { qty, amt, baseDate } = trendDashboardResult;

    for (const date of baseDate) {
      let qtyChartData = {
        type: QTY,
        yyyymmdd: date
      };

      let amtChartData = {
        type: AMT,
        yyyymmdd: date
      };

      const qtyMeasures = {};
      const amtMeasures = {};

      const activeMeasures = isEqual(measureType, QTY) ? IP_DASHBOARD_MEASURES_QTY : IP_DASHBOARD_MEASURES_AMT;
      for (const measure of activeMeasures) {
        const qtyFilterData = qty?.filter(data => data.measure === measure)[0];
        const amtFilterData = amt?.filter(data => data.measure === measure)[0];

        qtyMeasures[measure] = qtyFilterData?.[date] ?? 0;
        amtMeasures[measure] = amtFilterData?.[date] ?? 0;
      }
      qtyChartData = { ...qtyChartData, ...qtyMeasures };
      amtChartData = { ...amtChartData, ...amtMeasures };

      chartData.push(qtyChartData);
      chartData.push(amtChartData);
    }

    return groupBy(chartData, "type");
  }


  const retrieveIpDashboardData = () => {
    const params = getRequestParams();
    dashboardService.getIpModule(params)
      .then((res) => {
        const { data } = res;
        const itemHrchyYtdData = data?.[ITEM_HRCHY_YTD];
        const locationYtdData = data?.[LOCATION_YTD];
        const monthlyTrendData = data?.[MONTHLY_TREND];
        setCurrency(data?.currency);

        const headers = monthlyTrendData?.localDateHeader;

        const ytdDashboardData = setYtdData(itemHrchyYtdData, locationYtdData);

        setYtdDashboardResult(ytdDashboardData);

        const trendDataResult = monthlyTrendData?.data;
        const trendDashboardResult = setMonthlyByTrendData(trendDataResult, headers);
        const chartData = transformTrendChartData(trendDashboardResult);

        setTrendDataResult(trendDashboardResult);
        setTrendChartResult(chartData);
      })
      .catch((err) => {
        console.log(err);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { location: locationFilter, filter: filterValue, measureType });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  }

  useEffect(() => {
    setIsLoading(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading) {
      retrieveIpDashboardData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue?.category, locationFilter, isLoading]);

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { measureType });
    reduxDispatch(reduxUtil.updateViewState(reduxState));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureType]);

  const period = useMemo(() => baseDatesToPeriod(trendDataResult?.baseDate), [trendDataResult]);

  return (
    <>
      <FilterContainer>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent />
        <IPLocationFilter viewName={viewName} locationValue={locationFilter} onChangeFilter={setLocationFilter} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TypeToggleButton toggleType="measure" initialValue={measureType} toggleValue={setMeasureType} styles={{ mb: 1}}/>
          <DashboardPeriodCaption period={period} />
        </Box>
      </FilterContainer>      <DashboardComponent
        viewName={viewName}
        title={title}
        moduleCd={IP}
        stackBarData={ytdDashboardResult}
        stackBarColumDefs={stackBarColumDefs}
        allMeasures={ipDashboardMeasures}
        chartData={trendChartResult}
        gridData={trendDataResult}
        measureType={measureType}
        currencyCd={currency}
      />
    </>
  )
};
export default withTranslation()(IPDashboard);
